import { AccessToken } from '../../../utils/authUtils'
import axios from '../../../utils/axiosWithRetry'
import { toTargetLanguageCode } from './languageCodes'
import { TranslatableText, TranslationRequest } from './types'

// The deployed /v1/researcher-tools prefix is an Istio ingress pathPrefix that
// is stripped before the request reaches the app. The /v1 is infrastructure
// routing, not an API version; the app's canonical endpoint is /translate.
const getTranslationsEndpoint = () => {
  const baseUrl = process.env.REACT_APP_RESEARCHER_TOOLS_URL
  if (!baseUrl)
    throw new MissingConfigurationError(
      'REACT_APP_RESEARCHER_TOOLS_URL is not set. Set it in your .env or CI environment.'
    )
  return `${baseUrl}/translate`
}

export class MissingConfigurationError extends Error {
  constructor(detail: string) {
    super(detail)
    this.name = 'MissingConfigurationError'
  }
}

type ErrorResponse = {
  detail: string
}

export class TranslationServiceError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'TranslationServiceError'
    this.status = status
    this.detail = detail
  }
}

export class InvalidTranslationRequestError extends TranslationServiceError {
  constructor(detail: string) {
    super(400, detail)
    this.name = 'InvalidTranslationRequestError'
  }
}

export class TranslationValidationError extends TranslationServiceError {
  constructor(detail: string) {
    super(422, detail)
    this.name = 'TranslationValidationError'
  }
}

export class TranslationInternalError extends TranslationServiceError {
  constructor(detail: string) {
    super(500, detail)
    this.name = 'TranslationInternalError'
  }
}

export class TranslationGatewayError extends TranslationServiceError {
  constructor(detail: string) {
    super(502, detail)
    this.name = 'TranslationGatewayError'
  }
}

type HttpError = {
  response?: {
    status?: number
    data?: Partial<ErrorResponse>
  }
}

const isHttpError = (error: unknown): error is HttpError =>
  typeof error === 'object' && error !== null && 'response' in error

const mapTranslationError = (error: unknown): never => {
  if (isHttpError(error)) {
    const status = error.response?.status
    const detail = error.response?.data?.detail

    if (typeof detail === 'string') {
      if (status === 400) {
        throw new InvalidTranslationRequestError(detail)
      }
      if (status === 422) {
        throw new TranslationValidationError(detail)
      }
      if (status === 500) {
        throw new TranslationInternalError(detail)
      }
      if (status === 502) {
        throw new TranslationGatewayError(detail)
      }
    }
  }

  throw error
}

export default class ResearcherToolsTranslations {
  readonly translationsEndpoint: string

  constructor() {
    this.translationsEndpoint = getTranslationsEndpoint()
  }

  async translate(request: TranslationRequest): Promise<TranslatableText[]> {
    const targetLanguageCode = toTargetLanguageCode(
      request.target_language_code
    )

    try {
      const response = await axios.post<TranslatableText[]>(
        this.translationsEndpoint,
        {
          ...request,
          target_language_code: targetLanguageCode
        },
        {
          headers: {
            ...AccessToken.requestHeaders()
          }
        }
      )

      return response.data
    } catch (error) {
      return mapTranslationError(error)
    }
  }
}
